/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Building2, Package, CheckCircle2, Receipt, Search, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/ConfirmModal";
import PickupStatusBadge, { computePickupStatus, daysSinceInvoiced } from "@/components/PickupStatusBadge";

const STAGES = ["To Invoice", "Completed"];
const STAGE_LABEL = {
  "To Invoice": "To Invoice",
  "Completed":  "Invoiced",
};
const STAGE_ACCENT = {
  "To Invoice": "border-l-amber-500",
  "Completed":  "border-l-emerald-600",
};
// "Recently invoiced" = Completed orders invoiced in the last 7 days
const RECENT_DAYS = 7;

/**
 * Invoicing kanban — receives orders after admin clicks "Mark Invoiced"
 * on the Orders Pipeline. Two columns:
 *   Invoiced  → admin has issued the invoice; awaiting payment
 *   Paid      → customer has paid; archived from active work
 *
 * Admin-only. Drag-and-drop between columns mirrors the Orders Pipeline UX.
 */
export default function InvoicingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const isAdmin = user.role === "admin";

  const [orders, setOrders] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dragging, setDragging] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [q, setQ] = useState("");
  const [pickupFilter, setPickupFilter] = useState("all"); // all | awaiting | complete
  // The Invoiced column gets noisy as time passes — collapse it to a narrow
  // strip. Persisted in localStorage so the choice sticks across sessions.
  const [completedMinimised, setCompletedMinimised] = useState(() => {
    const saved = typeof window !== "undefined" && localStorage.getItem("invoicing-completed-minimised");
    return saved === "1";
  });
  const toggleCompletedMinimised = () => {
    setCompletedMinimised((v) => {
      const next = !v;
      try { localStorage.setItem("invoicing-completed-minimised", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, p, d] = await Promise.all([
        api.get("/orders").then((r) => r.data),
        api.get("/companies").then((r) => r.data),
        api.get("/inventory").then((r) => r.data),
        api.get("/dockets").then((r) => r.data),
      ]);
      const cutoff = new Date(Date.now() - RECENT_DAYS * 24 * 3600 * 1000).toISOString();
      // Keep all "To Invoice" + only RECENTLY-invoiced "Completed" orders
      setOrders(o.filter((x) =>
        x.Order_Status === "To Invoice" ||
        (x.Order_Status === "Completed" && (x.Invoiced_Date || "") >= cutoff),
      ));
      setDockets(d);
      setCompanies(c);
      setProducts(p);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookups = useMemo(() => ({
    company: Object.fromEntries(companies.map((c) => [c.id, c])),
    product: Object.fromEntries(products.map((p) => [p.id, p])),
  }), [companies, products]);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (o) => {
      if (!needle) return true;
      const co = lookups.company[o.Company_ID]?.Company_Name || "";
      const pr = lookups.product[o.Product_ID]?.Product_Name || "";
      const id = (o.id || "").toLowerCase();
      return [
        co, pr, id, o.Order_Reference, o.Invoice_Number,
      ].filter(Boolean).join(" ").toLowerCase().includes(needle);
    };
    const matchesPickup = (o) => {
      if (pickupFilter === "all") return true;
      // Filter only applies to Invoiced ("Completed") cards — "To Invoice"
      // cards are about to be invoiced, pickup state isn't actionable here.
      if (o.Order_Status !== "Completed") return true;
      const ps = computePickupStatus(o.id, dockets);
      return pickupFilter === "awaiting"
        ? ps === "awaiting"
        : ps === "complete" || ps === "cancelled" || ps === "none";
    };
    const g = {};
    for (const s of STAGES) g[s] = [];
    for (const o of orders) {
      if (!matches(o)) continue;
      if (!matchesPickup(o)) continue;
      if (g[o.Order_Status]) g[o.Order_Status].push(o);
    }
    return g;
  }, [orders, dockets, q, pickupFilter, lookups]);

  // Count invoiced-but-awaiting-pickup for the filter-pill badge — uses the
  // full orders list, not the filtered one, so the count never lies.
  const awaitingPickupCount = useMemo(() => orders.filter(
    (o) => o.Order_Status === "Completed" && computePickupStatus(o.id, dockets) === "awaiting",
  ).length, [orders, dockets]);

  const onDragStart = (e, id) => { setDragging(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, stage) => { if (dragging) { e.preventDefault(); setHoverCol(stage); } };
  const onDrop = async (e, stage) => {
    e.preventDefault();
    setHoverCol(null);
    if (!dragging) return;
    const id = dragging;
    setDragging(null);
    const o = orders.find((x) => x.id === id);
    if (!o || o.Order_Status === stage) return;
    try {
      const { data } = await api.patch(`/orders/${id}`, { Order_Status: stage });
      setOrders((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success(`Moved to ${stage}.`);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const markInvoiced = async (o) => {
    const ok = await confirm({
      title: "Mark invoice issued?",
      message: `Order #${o.id.slice(0, 8)} will move to the Invoiced column. This records that the invoice has been issued to the customer.`,
      confirmLabel: "Mark invoiced",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/orders/${o.id}/mark-invoiced`);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
      toast.success("Marked invoiced.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="space-y-2">
          <div className="es-overline text-brand-600 mb-2">// admin · invoicing</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Invoicing</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {grouped["To Invoice"].length} to invoice · {grouped.Completed.length} invoiced
            {q && <> · filtered by &ldquo;<span className="font-mono">{q}</span>&rdquo;</>}
          </p>
          {/* Pickup-status filter pills — only meaningful for the Invoiced column */}
          <div className="flex items-center gap-1.5 pt-1" data-testid="invoicing-pickup-filter">
            <span className="text-[10px] uppercase font-bold tracking-wider text-zinc-400 mr-1">Pickup:</span>
            {[
              { key: "all",      label: "All" },
              { key: "awaiting", label: `Awaiting (${awaitingPickupCount})` },
              { key: "complete", label: "Picked up" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setPickupFilter(opt.key)}
                data-testid={`invoicing-pickup-filter-${opt.key}`}
                className={[
                  "text-xs font-semibold rounded-full px-2.5 py-0.5 border transition-colors",
                  pickupFilter === opt.key
                    ? "bg-brand-600 text-white border-brand-600"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400",
                ].join(" ")}
              >{opt.label}</button>
            ))}
          </div>
        </div>
        <div className="relative w-72">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="invoicing-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order #, company, product, invoice…"
            className="es-input pl-9 pr-9 h-10"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-zinc-700"
              aria-label="Clear search"
              data-testid="invoicing-search-clear"
            ><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      {loading ? (
        <div className="text-center py-12 font-mono text-xs text-zinc-500">Loading…</div>
      ) : (
        <div className={`grid grid-cols-1 gap-4 ${completedMinimised ? "md:grid-cols-[1fr_64px]" : "md:grid-cols-2"}`}>
          {STAGES.map((stage) => {
            const items = grouped[stage] || [];
            const isCompleted = stage === "Completed";
            const minimised = isCompleted && completedMinimised;
            if (minimised) {
              return (
                <div
                  key={stage}
                  onDragOver={(e) => onDragOver(e, stage)}
                  onDrop={(e) => onDrop(e, stage)}
                  onDragLeave={() => setHoverCol((s) => (s === stage ? null : s))}
                  onClick={toggleCompletedMinimised}
                  title="Expand Invoiced column"
                  className={`rounded-xl bg-zinc-50 border flex flex-col items-center py-3 gap-3 cursor-pointer hover:bg-white hover:border-brand-400 transition-colors ${hoverCol === stage ? "border-brand-500 ring-2 ring-brand-200" : "border-zinc-200"}`}
                  data-testid={`invoicing-col-${stage.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleCompletedMinimised(); }}
                    data-testid="invoicing-completed-expand"
                    className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-white"
                    aria-label="Expand Invoiced column"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <div
                    className="font-display font-semibold text-[11px] text-zinc-700 tracking-wider"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >{STAGE_LABEL[stage]}</div>
                  <div className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-white text-zinc-600 border border-zinc-200 mt-auto">{items.length}</div>
                </div>
              );
            }
            return (
              <div
                key={stage}
                onDragOver={(e) => onDragOver(e, stage)}
                onDrop={(e) => onDrop(e, stage)}
                onDragLeave={() => setHoverCol((s) => (s === stage ? null : s))}
                className={`rounded-xl bg-zinc-50 border ${hoverCol === stage ? "border-brand-500 ring-2 ring-brand-200" : "border-zinc-200"} transition-colors`}
                data-testid={`invoicing-col-${stage.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="px-3 py-2.5 border-b border-zinc-200/70 flex items-center justify-between">
                  <div className="font-display font-semibold text-[13px] text-zinc-700 inline-flex items-center gap-2">
                    {stage === "To Invoice" ? <Receipt className="w-4 h-4 text-amber-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    {STAGE_LABEL[stage]}
                    {stage === "Completed" && (
                      <span className="text-[10px] font-normal text-zinc-400 ml-1">(last {RECENT_DAYS} days)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-white text-zinc-600 border border-zinc-200">{items.length}</div>
                    {isCompleted && (
                      <button
                        type="button"
                        onClick={toggleCompletedMinimised}
                        data-testid="invoicing-completed-minimise"
                        className="p-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-white"
                        aria-label="Minimise Invoiced column"
                        title="Minimise Invoiced column"
                      ><ChevronsRight className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[160px]">
                  {items.map((o) => {
                    const co = lookups.company[o.Company_ID];
                    const pr = lookups.product[o.Product_ID];
                    return (
                      <div
                        key={o.id}
                        draggable={isAdmin}
                        onDragStart={(e) => onDragStart(e, o.id)}
                        data-testid={`invoicing-card-${o.id}`}
                        className={`bg-white border border-zinc-200 border-l-4 ${STAGE_ACCENT[stage]} p-3 rounded-lg hover:border-brand-400 hover:shadow-md transition-all`}
                      >
                        <div onClick={() => navigate(`/orders/${o.id}`)} className="cursor-pointer">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="font-display font-semibold text-sm leading-tight text-zinc-900 truncate">{pr?.Product_Name || "—"}</div>
                            <div className="es-badge es-badge-brand flex-shrink-0">{o.Quantity_Ordered}</div>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-zinc-600 mb-1">
                            <Building2 className="w-3 h-3" />{co?.Company_Name || "Unknown"}
                          </div>
                          {o.Invoiced_Date && (
                            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                              <Receipt className="w-3 h-3" /> Invoiced {o.Invoiced_Date.slice(0, 10)}
                            </div>
                          )}
                          {stage === "Completed" && (
                            <div className="mt-2">
                              <PickupStatusBadge
                                status={computePickupStatus(o.id, dockets)}
                                days={daysSinceInvoiced(o)}
                              />
                            </div>
                          )}
                          {o.Notes && (
                            <div className="mt-2 px-2 py-1 rounded-md bg-amber-50 border-l-2 border-amber-400 text-[11px] text-amber-900 italic leading-snug line-clamp-2" title={o.Notes}>
                              {o.Notes}
                            </div>
                          )}
                        </div>
                        {stage === "To Invoice" && isAdmin && (
                          <div className="flex items-center mt-2 pt-2 border-t border-zinc-100">
                            <button
                              data-testid={`invoicing-mark-invoiced-${o.id}`}
                              onClick={(e) => { e.stopPropagation(); markInvoiced(o); }}
                              className="ml-auto text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Mark Invoiced
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-[11px] text-zinc-400 text-center py-6">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
